/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import { IQueryResultSettings } from '@msdyn365-commerce/retail-proxy';
import { buildCacheKey, QueryResultSettingsProxy } from '@msdyn365-commerce-modules/retail-actions';
import { getModelsForBySearchTermV2Async } from './DataActionExtension.g';
import { IAriModelResponseEntity } from './DataServiceEntities.g';
import { ICustomConfigurationExtension } from '../utils/CustomConfigurationExtension';
type pageType = 'Category' | 'Search';
/**
 * SearchModels Input Action
 */
export class SearchModelsInput implements Msdyn365.IActionInput {
    public searchText: string | undefined;
    public queryResultSettings: IQueryResultSettings;
    public pageType: pageType;
    public apiSettings: Msdyn365.ICommerceApiSettings;
    constructor(
        searchText: string | undefined,
        queryResultSettings: IQueryResultSettings,
        pageType: pageType,
        apiSettings: Msdyn365.ICommerceApiSettings
    ) {
        this.pageType = pageType;
        this.queryResultSettings = queryResultSettings;
        this.searchText = searchText;
        this.apiSettings = apiSettings;
    }

    // TODO: Determine if the results of this get action should cache the results and if so provide
    // a cache object type and an appropriate cache key
    public getCacheKey = () => {
        const queryResultSettings = this.queryResultSettings.Paging ? this.queryResultSettings.Paging : null;
        if (queryResultSettings) {
            const cacheKey = buildCacheKey(
                `${this.pageType}-${this.searchText}${queryResultSettings.Top}-${queryResultSettings.Skip}`,
                this.apiSettings
            );
            return cacheKey;
        } else {
            return buildCacheKey(`${this.pageType}-${this.searchText}`, this.apiSettings);
        }
    };
    public getCacheObjectType = () => 'ariModel';
    public dataCacheType = (): Msdyn365.CacheType => 'request';
}

// TODO: Create a data model here or import one to capture the response of the action
export interface ISearchModelsData {
    ariSearchResults: IAriModelResponseEntity;
}

/**
 * TODO: Use this function to create the input required to make the action call
 */
const createModelSearchInput = (args: Msdyn365.ICreateActionContext<{ itemsPerPage: number }>): Msdyn365.IActionInput => {
    const pageType =
        args.requestContext.urlTokens.pageType === 'Category' || (args.requestContext.query && args.requestContext.query.categoryId)
            ? 'Category'
            : 'Search';
    const queryResultSettings = QueryResultSettingsProxy.fromInputData(args).QueryResultSettings;
    if (queryResultSettings && queryResultSettings.Paging) {
        queryResultSettings.Paging.Top = args?.config?.itemsPerPage || 10;
    }
    return new SearchModelsInput(args.requestContext.query?.q || '', queryResultSettings, pageType, args.requestContext.apiSettings);
};

/**
 * TODO: Use this function to call your action and process the results as needed
 */
async function action(input: SearchModelsInput, ctx: Msdyn365.IActionContext): Promise<ISearchModelsData> {
    if (input.pageType === 'Search') {
        const appConfig = ctx.requestContext.app.config as ICustomConfigurationExtension;
        console.log(appConfig.websiteAriBrand);
        const response = await getModelsForBySearchTermV2Async(
            { callerContext: ctx, queryResultSettings: input.queryResultSettings },
            input.searchText!,
            appConfig.websiteAriBrand?.length ? appConfig.websiteAriBrand.split(',') : []
        );
        if (response && response.length) {
            return { ariSearchResults: response[0] };
        }
    }
    return { ariSearchResults: { Models: [], TotalCount: 0 } };
}

export default Msdyn365.createObservableDataAction<ISearchModelsData>({
    action: <Msdyn365.IAction<ISearchModelsData>>action,
    id: 'SearchModels',
    input: createModelSearchInput
});
